// "Talk to us" sales qualifier: the modal on /pricing and /contact (markup in
// _includes/sales-qualifier.html). Asks who they are, how many licenses they
// need and whether they'd rather subscribe or pay up front, then routes them:
// teams at or above the meeting threshold get the booking calendar, everyone
// else the self-serve options (demo, trial, checkout, quote). All the copy is
// in the markup, so it can be translated: this only shows and hides it and
// fills in the visitor's name and license count. The only call
// to the cloud app is made when a visitor clicks through to book a call
// (POST /api/sales_inquiries), which tells sales on Slack.
import { Controller } from "stimulus"
import M from "../materialize"
import Email from "../lib/email"
import Honeybadger from "../honeybadger"

export default class SalesQualifierController extends Controller {

  static get targets() {
    return [
      'modal', 'content', 'stepOne', 'stepTwo', 'form', 'name', 'email', 'quantity', 'preference', 'plan', 'source', 'website',
      'error', 'errorMessage', 'result', 'meeting', 'selfServe', 'upfrontTip',
      'firstName', 'count', 'plural', 'preferenceText'
    ]
  }

  urlBase = window.jekyllEnv === 'production' ? 'https://app.beekeeperstudio.io' : 'http://localhost:3000'

  // Teams this size or bigger are offered a call. The page passes in
  // site.sales.meeting_threshold (_config.yml) as
  // data-sales-qualifier-threshold; this is only the fallback.
  meetingThreshold = 10
  modal = null

  connect() {
    const threshold = parseInt(this.data.get('threshold'), 10)
    if (threshold > 0) this.meetingThreshold = threshold
    if (this.hasModalTarget) {
      this.modal = M.Modal.init(this.modalTarget, { dismissible: true })
    }
  }

  disconnect() {
    if (this.modal) {
      this.modal.destroy()
      this.modal = null
    }
  }

  // Opens the modal. The trigger button can preselect the plan and payment
  // preference (data-plan, data-preference) and say where it lives on the
  // page (data-source) so the inquiry records which button was used.
  open(event) {
    if (event) event.preventDefault()
    const trigger = event ? event.currentTarget : null
    this.reset()
    if (trigger) {
      if (trigger.dataset.plan) this.planTarget.value = trigger.dataset.plan
      if (trigger.dataset.preference) this.setPreference(trigger.dataset.preference)
      if (trigger.dataset.source) this.sourceTarget.value = trigger.dataset.source
    }
    if (this.modal) this.modal.open()
    this.track('talk-to-us-open')
    setTimeout(() => this.nameTarget.focus(), 150)
  }

  close() {
    if (this.modal) this.modal.close()
  }

  reset() {
    if (this.hasContentTarget) this.contentTarget.style.minHeight = ''
    this.showStep(1)
    this.meetingTarget.style.display = 'none'
    this.selfServeTarget.style.display = 'none'
    this.hideError()
  }

  // Step two keeps the visitor's answers, so Back just swaps the panels.
  back(event) {
    if (event) event.preventDefault()
    this.showStep(1)
    this.hideError()
    this.nameTarget.focus()
  }

  // Swaps between the questions (1) and the suggested route (2), updating
  // the step indicator. Before leaving step one the content box is pinned to
  // its current height so the modal doesn't shrink and jump when the
  // shorter result panel replaces the form.
  showStep(step) {
    if (step === 2) this.lockHeight()
    this.formTarget.style.display = step === 1 ? '' : 'none'
    this.resultTarget.style.display = step === 2 ? '' : 'none'
    if (this.hasStepOneTarget) {
      this.stepOneTarget.classList.toggle('is-active', step === 1)
      this.stepOneTarget.classList.toggle('is-done', step === 2)
    }
    if (this.hasStepTwoTarget) {
      this.stepTwoTarget.classList.toggle('is-active', step === 2)
    }
    if (step === 2 && this.hasContentTarget) this.contentTarget.scrollTop = 0
  }

  lockHeight() {
    if (!this.hasContentTarget) return
    const height = this.contentTarget.offsetHeight
    if (height > 0) this.contentTarget.style.minHeight = `${height}px`
  }

  setPreference(value) {
    this.preferenceTargets.forEach((input) => {
      input.checked = input.value === value
    })
  }

  get preference() {
    const checked = this.preferenceTargets.find((input) => input.checked)
    return checked ? checked.value : 'subscription'
  }

  payload() {
    return {
      name: this.nameTarget.value.trim(),
      email: this.emailTarget.value.trim(),
      quantity: parseInt(this.quantityTarget.value, 10),
      payment_preference: this.preference,
      plan: this.planTarget.value || null,
      source: this.sourceTarget.value || null,
      page_url: window.location.href
    }
  }

  // Returns which answer needs fixing (the data-error of its message), or null.
  validate(payload) {
    if (!payload.name) return 'name'
    if (!Email.validate(payload.email)) return 'email'
    if (!(payload.quantity >= 1)) return 'quantity'
    return null
  }

  // Checks the answers and shows the route; nothing leaves the browser yet.
  submit(event) {
    event.preventDefault()
    this.hideError()
    const payload = this.payload()
    const problem = this.validate(payload)
    if (problem) {
      this.showError(problem)
      return
    }
    const route = payload.quantity >= this.meetingThreshold ? 'meeting' : 'self_serve'
    this.showResult(route, payload)
  }

  showResult(route, payload) {
    const firstName = payload.name.split(' ')[0]
    const upfront = payload.payment_preference === 'upfront'
    const plural = this.pluralCategory(payload.quantity)

    this.firstNameTargets.forEach((el) => { el.textContent = firstName })
    this.countTargets.forEach((el) => { el.textContent = payload.quantity })
    // Languages without a "one" form fall back to "other".
    const plurals = this.pluralTargets.map((el) => el.dataset.plural)
    const shown = plurals.includes(plural) ? plural : 'other'
    this.pluralTargets.forEach((el) => { el.hidden = el.dataset.plural !== shown })
    this.preferenceTextTargets.forEach((el) => {
      el.hidden = el.dataset.preference !== payload.payment_preference
    })

    // Only one route panel at a time: a visitor can go Back, change their
    // answers and land on the other route.
    const meeting = route === 'meeting'
    this.meetingTarget.style.display = meeting ? '' : 'none'
    this.selfServeTarget.style.display = meeting ? 'none' : ''

    if (meeting) {
      this.track('talk-to-us-meeting')
    } else {
      this.upfrontTipTargets.forEach((el) => {
        el.style.display = upfront ? '' : 'none'
      })
      this.track('talk-to-us-self-serve')
    }
    this.showStep(2)
  }

  // The visitor is heading to the booking calendar (the link's own click
  // opens it in a new tab). Send their answers to the cloud app, which posts
  // them to the leads Slack channel so sales hears about the inquiry before
  // the calendar invite arrives. Best effort: the calendar opens either way,
  // and keepalive lets the request finish even if this page is left.
  bookCall() {
    this.track('talk-to-us-book-call')
    const report = (error) => {
      console.error(error)
      Honeybadger.notify(error)
    }
    try {
      fetch(`${this.urlBase}/api/sales_inquiries`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ sales_inquiry: this.payload(), website: this.websiteTarget.value })
      }).then((response) => {
        if (!response.ok) throw new Error(`sales inquiry request failed with status ${response.status}`)
      }).catch(report)
    } catch (error) {
      report(error)
    }
  }

  // The plural category (one, few, many, other...) of a number in the
  // page's language.
  pluralCategory(count) {
    try {
      return new Intl.PluralRules(document.documentElement.lang || 'en').select(count)
    } catch (error) {
      return count === 1 ? 'one' : 'other'
    }
  }

  showError(problem) {
    this.errorMessageTargets.forEach((el) => {
      el.hidden = el.dataset.error !== problem
    })
    this.errorTarget.style.display = ''
  }

  hideError() {
    this.errorMessageTargets.forEach((el) => { el.hidden = true })
    this.errorTarget.style.display = 'none'
  }

  track(eventName) {
    try {
      if (window.isDevelopment) {
        console.log('DEV TRACK:', eventName)
      } else if (window.fathom) {
        window.fathom.trackEvent(eventName)
      }
    } catch (error) {
      Honeybadger.notify(error)
    }
  }
}
